// src/sot/primitives/AvatarThumb68.jsx
import React from "react";

const DOCTOR_PROFILE_PLACEHOLDER = "/icons/doctor/avatar_placeholder.svg";
const PATIENT_PROFILE_PLACEHOLDER = "/icons/Profile_Avatar.svg";
const ROW_AVATAR_PLACEHOLDER = "/icons/circular-avatar-placeholder.svg";

function cleanSrc(value) {
  return String(value || "").trim();
}

function isProfileCardPlaceholder(value) {
  const src = cleanSrc(value);
  return (
    src === DOCTOR_PROFILE_PLACEHOLDER ||
    src === PATIENT_PROFILE_PLACEHOLDER ||
    src === "/icons/doctor/doctor_profile.jpg" ||
    src === "/icons/doctor/doctor_profile.png"
  );
}

function resolveFallback({ shape, role, fallbackSrc }) {
  const explicitFallback = cleanSrc(fallbackSrc);
  if (explicitFallback) return explicitFallback;
  if (shape === "circle") return ROW_AVATAR_PLACEHOLDER;
  return role === "patient" ? PATIENT_PROFILE_PLACEHOLDER : DOCTOR_PROFILE_PLACEHOLDER;
}

export default function AvatarThumb68({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  style,
  size = 68,
  shape = "square",
  role = "doctor",
  fallbackSrc = "",
  visualTreatment = "vivid",
  onImageError,
  onImageLoad,
}) {
  const normalizedSrc = cleanSrc(src);
  const fallback = resolveFallback({ shape, role, fallbackSrc });
  const safeFallback =
    shape === "circle" && isProfileCardPlaceholder(fallback)
      ? ROW_AVATAR_PLACEHOLDER
      : fallback;

  const shouldSwapToRowPlaceholder =
    shape === "circle" && isProfileCardPlaceholder(normalizedSrc || safeFallback);
  const safeSrc =
    normalizedSrc && !shouldSwapToRowPlaceholder ? normalizedSrc : safeFallback;
  const hasCustomPhoto =
    Boolean(normalizedSrc) &&
    normalizedSrc !== ROW_AVATAR_PLACEHOLDER &&
    !isProfileCardPlaceholder(normalizedSrc);
  const isCircle = shape === "circle";

  const frameClassName =
    isCircle && visualTreatment !== "plain"
      ? `
        rounded-full
        overflow-hidden
        shrink-0
        bg-blend-overlay
        bg-[linear-gradient(180deg,rgba(255,255,255,0.80)_0%,rgba(255,255,255,0.00)_100%)]
        shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]
      `
      : isCircle
        ? `
        rounded-full
        overflow-hidden
        shrink-0
      `
        : `
        shrink-0
      `;
  const frameStyle =
    Number.isFinite(Number(size)) && Number(size) > 0
      ? { width: Number(size), height: Number(size), ...style }
      : style;

  return (
    <div
      className={[frameClassName, className].filter(Boolean).join(" ")}
      style={frameStyle}
    >
      <img
        src={safeSrc}
        alt={alt}
        className={[
          "h-full w-full",
          isCircle ? "rounded-full object-cover" : "",
          !isCircle && `afrimed-avatar-square ${hasCustomPhoto ? "object-cover" : "object-contain p-[2px]"}`,
          imgClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        draggable="false"
        onLoad={(event) => onImageLoad?.(event)}
        onError={(e) => {
          onImageError?.(e);
          e.currentTarget.onerror = null;
          e.currentTarget.src = safeFallback;
        }}
      />
    </div>
  );
}
